import React, { useState } from 'react';
import { post } from '@aws-amplify/api';
import { useTranslation } from 'react-i18next';

const personTypeOrder = ['Adult', '13-17', '8-12', '4-7', '0-3'];

function AddParticipantsForm({ formData, updateFormData }) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [error, setError] = useState(null);
  const [draggedParticipant, setDraggedParticipant] = useState(null);
  const [newFamilyUnitName, setNewFamilyUnitName] = useState('');
  const [newParticipantAge, setNewParticipantAge] = useState('Adult');
  const [editingEmailId, setEditingEmailId] = useState(null);
  const [editingEmailValue, setEditingEmailValue] = useState('');
  const [editingNameId, setEditingNameId] = useState(null);
  const [editingNameValue, setEditingNameValue] = useState('');
  const [nameEditError, setNameEditError] = useState(null);

  const handleAddParticipant = async (e) => {
    e.preventDefault();
    if (name) {
      if (formData.participants.find((p) => p.name === name)) {
        setError(t('participants.errors.duplicateName'));
        return;
      }
      const isGiver = formData.eventType === 'kris-kindle' ? newParticipantAge === 'Adult' : true;
      const newParticipant = { id: name, name, email, age: newParticipantAge, sex: 'Unknown', isGiver };
      const newParticipants = [...formData.participants, newParticipant];
      updateFormData({ participants: newParticipants });
      setName('');
      setEmail('');
      setError(null);

      try {
        const response = await post({
          apiName: 'MyFastApi',
          path: '/determine-sex',
          options: { 
            body: { name: name.split(' ')[0] },
          }
        });
        const newSex = response.sex;
        const updatedParticipants = newParticipants.map(p => p.id === newParticipant.id ? { ...p, sex: newSex } : p);
        updateFormData({ participants: updatedParticipants });
      } catch (error) {
        console.error('Error determining sex:', error);
      }
    }
  };

  const handleCsvChange = (e) => {
    updateFormData({ csvData: e.target.value });
  };

  const handleParseCsv = () => {
    const lines = formData.csvData.split('\n');
    const newParticipants = [];
    const duplicates = [];
    const newFamilyUnits = { ...formData.familyUnits };
    let currentFamilyUnit = null;
    let familyMemberCount = 0;

    lines.forEach((line) => {
      const trimmedLine = line.trim();
      const upperLine = trimmedLine.toUpperCase();

      // Accept FAMILY, TEAM, or CLASSROOM prefixes
      if (upperLine.startsWith('FAMILY ') || upperLine.startsWith('TEAM ') || upperLine.startsWith('CLASSROOM ')) {
        const prefixLength = upperLine.startsWith('FAMILY ') ? 7 : upperLine.startsWith('TEAM ') ? 5 : 10;
        currentFamilyUnit = trimmedLine.substring(prefixLength).trim();
        if (currentFamilyUnit && !newFamilyUnits[currentFamilyUnit]) {
          newFamilyUnits[currentFamilyUnit] = [];
        }
        familyMemberCount = 0;
      } else {
        const [name, email] = trimmedLine.split(',');
        if (name) {
          const trimmedName = name.trim();
          if (trimmedName) {
            if (formData.participants.find((p) => p.name === trimmedName) || newParticipants.find((p) => p.name === trimmedName)) {
              duplicates.push(trimmedName);
            } else {
              let age = 'Adult';
              if (currentFamilyUnit) {
                familyMemberCount++;
                if (familyMemberCount > 2) {
                  age = '13-17';
                }
              }
              const isGiver = formData.eventType === 'kris-kindle' ? age === 'Adult' : true;
              const participant = { id: trimmedName, name: trimmedName, age, email: email ? email.trim() : '', sex: 'Unknown', isGiver };
              newParticipants.push(participant);
              if (currentFamilyUnit) {
                newFamilyUnits[currentFamilyUnit].push(participant);
              }
            }
          }
        }
      }
    });

    if (duplicates.length > 0) {
      setError(t('participants.errors.duplicatesFound', { names: duplicates.join(', ') }));
    } else {
      setError(null);
    }

    updateFormData({
      participants: [...formData.participants, ...newParticipants], 
      familyUnits: newFamilyUnits,
      csvData: '' 
    });
  };

  const handleRemoveParticipant = (participantId) => {
    const newParticipants = formData.participants.filter(p => p.id !== participantId);
    const newFamilyUnits = { ...formData.familyUnits };
    for (const unitName in newFamilyUnits) {
      newFamilyUnits[unitName] = newFamilyUnits[unitName].filter(p => p.id !== participantId);
    }
    updateFormData({ participants: newParticipants, familyUnits: newFamilyUnits });
  };

  const handleAgeChange = (participantId, age) => {
    const newParticipants = formData.participants.map(p => p.id === participantId ? { ...p, age } : p);
    const newFamilyUnits = { ...formData.familyUnits };
    for (const unitName in newFamilyUnits) {
      newFamilyUnits[unitName] = newFamilyUnits[unitName].map(p => p.id === participantId ? { ...p, age } : p);
    }
    updateFormData({ participants: newParticipants, familyUnits: newFamilyUnits });
  };

  const handleIsGiverChange = (participantId) => {
    const updatedParticipants = formData.participants.map(p =>
      p.id === participantId ? { ...p, isGiver: !p.isGiver } : p
    );
    console.log(`Toggling isGiver for participant ${participantId} to ${!formData.participants.find(p => p.id === participantId).isGiver}`);
    updateFormData({ participants: updatedParticipants });
  };

  const handleEmailClick = (participant) => {
    setEditingEmailId(participant.id);
    setEditingEmailValue(participant.email || '');
  };

  const handleEmailChange = (e) => {
    setEditingEmailValue(e.target.value);
  };

  const handleEmailSave = (participantId) => {
    const updatedParticipants = formData.participants.map(p =>
      p.id === participantId ? { ...p, email: editingEmailValue.trim() } : p
    );
    const newFamilyUnits = { ...formData.familyUnits };
    for (const unitName in newFamilyUnits) {
      newFamilyUnits[unitName] = newFamilyUnits[unitName].map(p =>
        p.id === participantId ? { ...p, email: editingEmailValue.trim() } : p
      );
    }
    updateFormData({ participants: updatedParticipants, familyUnits: newFamilyUnits });
    setEditingEmailId(null);
    setEditingEmailValue('');
  };

  const handleEmailKeyDown = (e, participantId) => {
    if (e.key === 'Enter') {
      handleEmailSave(participantId);
    } else if (e.key === 'Escape') {
      setEditingEmailId(null);
      setEditingEmailValue('');
    }
  };

  const handleNameClick = (participant) => {
    setEditingNameId(participant.id);
    setEditingNameValue(participant.name);
    setNameEditError(null);
  };

  const handleNameChange = (e) => {
    setEditingNameValue(e.target.value);
    setNameEditError(null);
  };

  const handleNameSave = (participantId) => {
    const trimmedName = editingNameValue.trim();

    // Validate name is not empty
    if (!trimmedName) {
      setNameEditError(t('participants.errors.nameRequired'));
      return;
    }

    // Check for duplicate names (excluding the current participant)
    const isDuplicate = formData.participants.some(
      p => p.id !== participantId && p.name === trimmedName
    );

    if (isDuplicate) {
      setNameEditError(t('participants.errors.duplicateName'));
      return;
    }

    // Update participant name and ID
    const updatedParticipants = formData.participants.map(p =>
      p.id === participantId ? { ...p, id: trimmedName, name: trimmedName } : p
    );

    // Update familyUnits
    const newFamilyUnits = { ...formData.familyUnits };
    for (const unitName in newFamilyUnits) {
      newFamilyUnits[unitName] = newFamilyUnits[unitName].map(p =>
        p.id === participantId ? { ...p, id: trimmedName, name: trimmedName } : p
      );
    }

    // Update rules if they exist
    const updatedRules = formData.rules ? { ...formData.rules } : {};
    if (updatedRules.individual) {
      // Update individual rules where this participant is involved
      const oldName = formData.participants.find(p => p.id === participantId)?.name;
      if (oldName) {
        // Update rules object keys
        if (updatedRules.individual[oldName]) {
          updatedRules.individual[trimmedName] = updatedRules.individual[oldName];
          delete updatedRules.individual[oldName];
        }
        // Update rules where this participant is a target
        Object.keys(updatedRules.individual).forEach(key => {
          if (Array.isArray(updatedRules.individual[key])) {
            updatedRules.individual[key] = updatedRules.individual[key].map(target =>
              target === oldName ? trimmedName : target
            );
          }
        });
      }
    }

    // Update inclusions if they exist
    const updatedInclusions = formData.inclusions ? { ...formData.inclusions } : {};
    if (Object.keys(updatedInclusions).length > 0) {
      const oldName = formData.participants.find(p => p.id === participantId)?.name;
      if (oldName) {
        // Update inclusions object keys
        if (updatedInclusions[oldName]) {
          updatedInclusions[trimmedName] = updatedInclusions[oldName];
          delete updatedInclusions[oldName];
        }
        // Update inclusions where this participant is a target
        Object.keys(updatedInclusions).forEach(key => {
          if (Array.isArray(updatedInclusions[key])) {
            updatedInclusions[key] = updatedInclusions[key].map(target =>
              target === oldName ? trimmedName : target
            );
          }
        });
      }
    }

    // Update assignments if they exist
    const updatedGeneratedAssignments = formData.generatedAssignments ? { ...formData.generatedAssignments } : null;
    if (updatedGeneratedAssignments) {
      const oldName = formData.participants.find(p => p.id === participantId)?.name;
      if (oldName) {
        // Update as giver
        if (updatedGeneratedAssignments[oldName]) {
          updatedGeneratedAssignments[trimmedName] = updatedGeneratedAssignments[oldName];
          delete updatedGeneratedAssignments[oldName];
        }
        // Update as receiver
        Object.keys(updatedGeneratedAssignments).forEach(giverName => {
          if (Array.isArray(updatedGeneratedAssignments[giverName])) {
            updatedGeneratedAssignments[giverName] = updatedGeneratedAssignments[giverName].map(receiver =>
              receiver === oldName ? trimmedName : receiver
            );
          }
        });
      }
    }

    updateFormData({
      participants: updatedParticipants,
      familyUnits: newFamilyUnits,
      rules: updatedRules,
      inclusions: updatedInclusions,
      generatedAssignments: updatedGeneratedAssignments,
    });

    setEditingNameId(null);
    setEditingNameValue('');
    setNameEditError(null);
  };

  const handleNameKeyDown = (e, participantId) => {
    if (e.key === 'Enter') {
      handleNameSave(participantId);
    } else if (e.key === 'Escape') {
      setEditingNameId(null);
      setEditingNameValue('');
      setNameEditError(null);
    }
  };

  const handleCloseError = () => {
    setError(null);
  };

  const handleDragStart = (e, participant) => {
    setDraggedParticipant(participant);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e, targetUnit) => {
    e.preventDefault();
    if (!draggedParticipant) return;

    const newFamilyUnits = { ...formData.familyUnits };

    for (const unitName in newFamilyUnits) {
      newFamilyUnits[unitName] = newFamilyUnits[unitName].filter(p => p.id !== draggedParticipant.id);
    }

    if (targetUnit) {
      if (!newFamilyUnits[targetUnit]) {
        newFamilyUnits[targetUnit] = [];
      }
      newFamilyUnits[targetUnit].push(draggedParticipant);
    } 

    updateFormData({ familyUnits: newFamilyUnits });
    setDraggedParticipant(null);
  };

  const handleCreateFamilyUnit = () => {
    if (newFamilyUnitName && !formData.familyUnits[newFamilyUnitName]) {
      const newFamilyUnits = { [newFamilyUnitName]: [], ...formData.familyUnits };
      updateFormData({ familyUnits: newFamilyUnits });
      setNewFamilyUnitName('');
    }
  };

  const handleRemoveFamilyUnit = (unitName) => {
    const newFamilyUnits = { ...formData.familyUnits };
    delete newFamilyUnits[unitName];
    updateFormData({ familyUnits: newFamilyUnits });
  };

  const getUnassignedParticipants = () => {
    const assignedIds = Object.values(formData.familyUnits).flat().map(p => p.id);
    return formData.participants.filter(p => !assignedIds.includes(p.id));
  };

  const sortParticipants = (participants) => {
    return participants.sort((a, b) => a.name.localeCompare(b.name));
  };

  const sortedParticipants = sortParticipants(formData.participants);

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-50 border-2 border-red-400 text-red-800 px-6 py-4 rounded-lg relative shadow-sm" role="alert">
          <div className="flex items-start">
            <span className="text-red-600 mr-3 text-xl">⚠️</span>
            <span className="block flex-1">{error}</span>
            <button
              onClick={handleCloseError}
              className="ml-4 text-red-600 hover:text-red-800 transition-colors"
            >
              <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                <path d="M14.348 14.849a1.2 1.2 0 0 1-1.697 0L10 11.818l-2.651 3.031a1.2 1.2 0 1 1-1.697-1.697l2.758-3.15-2.759-3.152a1.2 1.2 0 1 1 1.697-1.697L10 8.183l2.651-3.031a1.2 1.2 0 1 1 1.697 1.697l-2.758 3.152 2.758 3.15a1.2 1.2 0 0 1 0 1.698z"/>
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Add Individual Participant Section */}
      <div className="bg-white border border-gray-300 rounded-lg p-6 shadow-sm">
        <h3 className="text-xl font-bold text-gray-800 mb-4">{t('participants.addIndividual.title')}</h3>
        <p className="text-sm text-gray-600 mb-4">{t('participants.addIndividual.description')}</p>
        <form onSubmit={handleAddParticipant}>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
            <div className="md:col-span-4">
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t('participants.addIndividual.namePlaceholder')}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              />
            </div>
            <div className="md:col-span-4">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('participants.addIndividual.emailPlaceholder')}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              />
            </div>
            {formData.eventType === 'kris-kindle' && (
              <div className="md:col-span-2">
                <select
                  value={newParticipantAge}
                  onChange={(e) => setNewParticipantAge(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all bg-white"
                >
                  <option value="Adult">{t('ageGroups.Adult')}</option>
                  <option value="13-17">{t('ageGroups.13-17')}</option>
                  <option value="8-12">{t('ageGroups.8-12')}</option>
                  <option value="4-7">{t('ageGroups.4-7')}</option>
                  <option value="0-3">{t('ageGroups.0-3')}</option>
                </select>
              </div>
            )}
            <div className={formData.eventType === 'kris-kindle' ? "md:col-span-2" : "md:col-span-4"}>
              <button
                type="submit"
                className="w-full bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 px-6 rounded-lg transition-colors shadow-sm"
              >
                {t('participants.addIndividual.addButton')}
              </button>
            </div>
          </div>
        </form>
      </div>

      {/* Bulk Add Section */}
      <div className="bg-white border border-gray-300 rounded-lg p-6 shadow-sm">
        <h3 className="text-xl font-bold text-gray-800 mb-4">{t('participants.bulkAdd.title')}</h3>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
          <p className="text-sm text-blue-900">
            {formData.eventType === 'kris-kindle'
              ? t('participants.bulkAdd.instructions.krisKindle')
              : t('participants.bulkAdd.instructions.secretSanta')}
          </p>
          <pre className="text-xs text-blue-800 mt-2 font-mono">
{formData.eventType === 'kris-kindle'
  ? `FAMILY FamilyName\nMember 1,email@example.com\nMember 2,email@example.com`
  : `TEAM TeamName\nMember 1,email@example.com\nMember 2,email@example.com\n\nCLASSROOM ClassroomName\nStudent 1,email@example.com\nStudent 2,email@example.com`}
          </pre>
        </div>
        <textarea
          value={formData.csvData}
          onChange={handleCsvChange}
          placeholder={formData.eventType === 'kris-kindle'
            ? t('participants.bulkAdd.textareaPlaceholder.krisKindle')
            : t('participants.bulkAdd.textareaPlaceholder.secretSanta')}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all font-mono text-sm"
          rows="8"
        ></textarea>
        <button
          onClick={handleParseCsv}
          className="bg-green-500 hover:bg-green-600 text-white font-semibold py-2 px-6 rounded-lg mt-3 transition-colors shadow-sm"
        >
          {t('participants.bulkAdd.parseButton')}
        </button>
      </div>


      {/* Participants List Section */}
      <div className="bg-white border border-gray-300 rounded-lg p-6 shadow-sm">
        <h3 className="text-xl font-bold text-gray-800 mb-2">
          {t('participants.participantsList.title')} ({sortedParticipants.length})
        </h3>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
          <p className="text-sm text-blue-900">
            💡 <strong>Tip:</strong> {t('participants.participantsList.editTip')}
          </p>
        </div>
        {sortedParticipants.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <p className="text-lg mb-2">{t('participants.participantsList.emptyState.title')}</p>
            <p className="text-sm">{t('participants.participantsList.emptyState.description')}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-b-2 border-gray-300">
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">{t('participants.participantsList.nameColumn')}</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">{t('participants.participantsList.emailColumn')}</th>
                  {formData.eventType === 'kris-kindle' && (
                    <>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">{t('participants.participantsList.ageColumn')}</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">{t('participants.participantsList.giverBadge')}</th>
                    </>
                  )}
                  <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">{t('participants.participantsList.actionsColumn')}</th>
                </tr>
              </thead>
              <tbody>
                {sortedParticipants.map((p, index) => (
                  <tr
                    key={p.id}
                    className={`border-b border-gray-200 hover:bg-gray-50 transition-colors ${
                      index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                    }`}
                  >
                    <td className="py-3 px-4 text-sm font-medium text-gray-800">
                      {editingNameId === p.id ? (
                        <div>
                          <input
                            type="text"
                            value={editingNameValue}
                            onChange={handleNameChange}
                            onBlur={() => handleNameSave(p.id)}
                            onKeyDown={(e) => handleNameKeyDown(e, p.id)}
                            placeholder={t('participants.participantsList.editNamePlaceholder')}
                            autoFocus
                            className={`w-full px-2 py-1 border rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                              nameEditError ? 'border-red-500' : 'border-blue-500'
                            }`}
                          />
                          {nameEditError && (
                            <p className="text-xs text-red-600 mt-1">{nameEditError}</p>
                          )}
                        </div>
                      ) : (
                        <span
                          onClick={() => handleNameClick(p)}
                          className="cursor-pointer hover:text-blue-600 hover:underline"
                          title={t('participants.participantsList.editNameTitle')}
                        >
                          {p.name}
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600">
                      {editingEmailId === p.id ? (
                        <input
                          type="email"
                          value={editingEmailValue}
                          onChange={handleEmailChange}
                          onBlur={() => handleEmailSave(p.id)}
                          onKeyDown={(e) => handleEmailKeyDown(e, p.id)}
                          placeholder={t('participants.participantsList.editEmailPlaceholder')}
                          autoFocus
                          className="w-full px-2 py-1 border border-blue-500 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      ) : (
                        <span
                          onClick={() => handleEmailClick(p)}
                          className={`cursor-pointer hover:text-blue-600 hover:underline ${!p.email ? 'text-gray-400' : ''}`}
                          title={t('participants.participantsList.editEmailTitle')}
                        >
                          {p.email || '—'}
                        </span>
                      )}
                    </td>
                    {formData.eventType === 'kris-kindle' && (
                      <>
                        <td className="py-3 px-4">
                          <select
                            value={p.age}
                            onChange={(e) => handleAgeChange(p.id, e.target.value)}
                            className="px-3 py-1 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm bg-white"
                          >
                            <option value="Adult">{t('ageGroups.Adult')}</option>
                            <option value="13-17">{t('ageGroups.13-17')}</option>
                            <option value="8-12">{t('ageGroups.8-12')}</option>
                            <option value="4-7">{t('ageGroups.4-7')}</option>
                            <option value="0-3">{t('ageGroups.0-3')}</option>
                          </select>
                        </td>
                        <td className="py-3 px-4">
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={p.isGiver}
                              onChange={() => handleIsGiverChange(p.id)}
                              className="sr-only peer"
                            />
                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                          </label>
                        </td>
                      </>
                    )}
                    <td className="py-3 px-4 text-right">
                      <button
                        onClick={() => handleRemoveParticipant(p.id)}
                        className="bg-red-500 hover:bg-red-600 text-white font-medium py-1 px-3 rounded-md transition-colors text-sm"
                      >
                        {t('common.delete')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Family Units / Teams & Classrooms Section */}
      <div className="bg-white border border-gray-300 rounded-lg p-6 shadow-sm">
        <h3 className="text-xl font-bold text-gray-800 mb-2">
          {formData.eventType === 'kris-kindle'
            ? t('participants.familyUnits.title.krisKindle')
            : t('participants.familyUnits.title.secretSanta')}
        </h3>
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
          <p className="text-sm text-amber-900">
            {formData.eventType === 'kris-kindle'
              ? `ℹ️ ${t('participants.familyUnits.info.krisKindle')}`
              : `ℹ️ ${t('participants.familyUnits.info.secretSanta')}`}
          </p>
        </div>

        {/* Create Unit/Group */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3 mb-6">
          <div className="md:col-span-9">
            <input
              type="text"
              value={newFamilyUnitName}
              onChange={(e) => setNewFamilyUnitName(e.target.value)}
              placeholder={formData.eventType === 'kris-kindle'
                ? t('participants.familyUnits.inputPlaceholder.krisKindle')
                : t('participants.familyUnits.inputPlaceholder.secretSanta')}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
            />
          </div>
          <div className="md:col-span-3">
            <button
              onClick={handleCreateFamilyUnit}
              className="w-full bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 px-6 rounded-lg transition-colors shadow-sm"
            >
              {formData.eventType === 'kris-kindle'
                ? t('participants.familyUnits.addButton.krisKindle')
                : t('participants.familyUnits.addButton.secretSanta')}
            </button>
          </div>
        </div>

          {/* Units/Groups Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {/* Unassigned Participants */}
            <div
              className="border-2 border-dashed border-gray-300 rounded-lg p-4 bg-gray-50 hover:border-gray-400 transition-colors min-h-[200px]"
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, null)}
            >
              <h4 className="font-semibold text-gray-700 mb-3 flex items-center">
                <span className="mr-2">👤</span>
                {t('participants.familyUnits.unassignedTitle')} ({getUnassignedParticipants().length})
              </h4>
              <div className="space-y-2">
                {getUnassignedParticipants().length === 0 ? (
                  <p className="text-xs text-gray-500 italic">{t('participants.familyUnits.allAssignedMessage')}</p>
                ) : (
                  getUnassignedParticipants().map(p => (
                    <div
                      key={p.id}
                      className="p-2 border border-gray-300 rounded-md bg-white cursor-move hover:shadow-md transition-shadow"
                      draggable
                      onDragStart={(e) => handleDragStart(e, p)}
                    >
                      <span className="text-sm font-medium text-gray-800">{p.name}</span>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Family Units / Teams / Classrooms */}
            {Object.entries(formData.familyUnits).map(([unitName, members]) => (
              <div
                key={unitName}
                className="border-2 border-blue-300 rounded-lg p-4 bg-blue-50 hover:border-blue-400 transition-colors min-h-[200px]"
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, unitName)}
              >
                <div className="flex justify-between items-start mb-3">
                  <h4 className="font-semibold text-blue-900 flex-1 flex items-center">
                    <span className="mr-2">👨‍👩‍👧‍👦</span>
                    <span className="truncate">{unitName}</span>
                  </h4>
                  <button
                    onClick={() => handleRemoveFamilyUnit(unitName)}
                    className="text-red-600 hover:text-red-800 transition-colors ml-2 text-xs font-semibold"
                    title={formData.eventType === 'kris-kindle'
                      ? t('participants.familyUnits.removeTitle.krisKindle')
                      : t('participants.familyUnits.removeTitle.secretSanta')}
                  >
                    ✕
                  </button>
                </div>
                <div className="space-y-2">
                  {members.length === 0 ? (
                    <p className="text-xs text-blue-700 italic">{t('participants.familyUnits.dragInstruction')}</p>
                  ) : (
                    members.map(p => (
                      <div
                        key={p.id}
                        className="p-2 border border-blue-300 rounded-md bg-white cursor-move hover:shadow-md transition-shadow"
                        draggable
                        onDragStart={(e) => handleDragStart(e, p)}
                      >
                        <span className="text-sm font-medium text-gray-800">{p.name}</span>
                      </div>
                    ))
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

    </div>
  );
}

export default AddParticipantsForm;